import { t } from "adapters/i18n";
import axiosRetry from "axios-retry";
import BaseService from "services/BaseService";
import { createServiceHandler } from "services/ServiceHandler";
import {
  type FarmAndRetreatLink,
  type UserFromRequest,
  type UserManagementFilter,
  UserPermission,
} from "./userManagement.types";

export const URL_BASE = `${process.env.REACT_APP_ENDPOINT}usuarios/api/v1`;
export const BASE_URL_V2 = `${process.env.REACT_APP_ENDPOINT}usuarios/api/v2`;

type UserData = {
  name: string;
  login: string;
  email: string;
};

class UserService extends BaseService {
  async listUsers(
    limit: number,
    offset: number,
    filter: UserManagementFilter & { clientId: number },
  ): Promise<{ data: UserFromRequest[]; total: number }> {
    const requestFilter = {
      busca: filter.searchValue,
      idCliente: [filter.clientId],
      idFazenda: filter.farmIds,
      idRetiro: filter.retreatIds,
      permissao: filter.permissions,
      status: filter.status,
      usuarioAdministrador: false,
      usuarioInterno: false,
    };

    const filterParsed = Object.entries(requestFilter).reduce(
      (acc, [key, value]) => {
        const isEmpty =
          value == null ||
          value === "" ||
          (Array.isArray(value) && value.length === 0);

        if (isEmpty) {
          return acc;
        }

        return {
          ...acc,
          [key]: Array.isArray(value) ? JSON.stringify(value) : value,
        };
      },
      {},
    );

    const response = await this.get(`${BASE_URL_V2}/usuarios`, {
      limit,
      offset,
      ...filterParsed,
    });

    return response.data;
  }

  async getUserData(
    userId: number,
    clientId: number,
  ): Promise<UserFromRequest | null> {
    try {
      const { data: response } = await this.get(
        `${BASE_URL_V2}/usuarios/${userId}?idCliente=[${clientId}]`,
      );
      const userData = response?.data?.[0];

      if (!userData) return null;

      return userData;
    } catch {
      throw new Error(t("user:fetch-user-data-error"));
    }
  }

  async checkIfLoginAlreadyExists(login?: string | null): Promise<boolean> {
    if (!login) {
      return false;
    }

    const response = await this.get(
      `${process.env.REACT_APP_ENDPOINT}usuarios/api/v2/usuarios/verificaExistencia?username=${login}`,
    );

    return response?.data?.username?.exist ?? false;
  }

  async checkIfEmailAlreadyExists(email?: string | null): Promise<boolean> {
    if (!email) {
      return false;
    }

    const response = await this.get(
      `${process.env.REACT_APP_ENDPOINT}usuarios/api/v2/usuarios/verificaExistencia?email=${email}`,
    );
    return response?.data?.email?.exist ?? false;
  }

  async createUserWithRelations(
    userData: UserData,
    permissions: Record<UserPermission, boolean>,
    farmAndRetreatLinks: FarmAndRetreatLink[],
    clientId: number,
  ): Promise<void> {
    const user = await this.createUser(userData);

    const farmIds = farmAndRetreatLinks.map(({ farm }) => farm.id);

    await Promise.all([
      this.linkUserToClient(user.id, clientId),
      this.linkUserToFarms(user.id, farmIds),
      this.linkUserToRetreats(user.id, farmAndRetreatLinks),
      this.updateUserPermissions(userData.login, permissions),
    ]);
  }

  async editUserWithRelations(
    userId: number,
    userData: UserData,
    permissions: Record<UserPermission, boolean>,
    farmAndRetreatLinks: FarmAndRetreatLink[],
  ): Promise<void> {
    await this.updateUser(userId, userData);

    const farmIds = farmAndRetreatLinks.map(({ farm }) => farm.id);

    await this.linkUserToFarms(userId, farmIds);
    await this.linkUserToRetreats(userId, farmAndRetreatLinks);
    await this.updateUserPermissions(userData.login, permissions);
  }

  async createUser({
    email,
    login,
    name,
  }: Omit<UserData, "id">): Promise<{ id: number }> {
    try {
      const result = await this.post(`${BASE_URL_V2}/usuarios`, {
        Email: email,
        Login: login,
        Nome: name,
      });

      return { id: result.data.views.IdUsuario };
    } catch (_) {
      throw new Error(t("user:create-new-user-error"));
    }
  }

  async updateUser(
    id: number,
    { name, login, email }: UserData,
  ): Promise<void> {
    try {
      await this.put(`${URL_BASE}/usuarios/${id}`, {
        Email: email,
        Login: login,
        Nome: name,
      });
    } catch (_) {
      throw new Error(t("user:update-user-error"));
    }
  }

  async linkUserToClient(userId: number, clientId: number): Promise<void> {
    try {
      const axiosInstance = createServiceHandler();
      axiosRetry(axiosInstance, { retries: 5, retryDelay: () => 3000 });

      await axiosInstance.post(`${URL_BASE}/usuarios/${userId}/clientes`, {
        IdUsuario: userId,
        IdCliente: clientId,
      });
    } catch (_) {
      console.error("Error linking user to client", _);
      throw new Error(t("user:link-user-to-client-error"));
    }
  }

  async linkUserToFarms(userId: number, farmIds: number[]): Promise<void> {
    try {
      await this.post(
        `${BASE_URL_V2}/usuarios/${userId}/fazendas/bulk`,
        farmIds,
      );
    } catch (_) {
      throw new Error(t("user:link-user-to-farms-error"));
    }
  }

  async linkUserToRetreats(
    userId: number,
    links: FarmAndRetreatLink[],
  ): Promise<void> {
    try {
      const retreatsToAssociate = links.flatMap(({ farm, retreats }) =>
        retreats.map((retreat) => ({
          IdFazenda: farm.id,
          IdRetiro: retreat.id,
        })),
      );

      await this.post(
        `${BASE_URL_V2}/usuarios/${userId}/retiros/bulk`,
        retreatsToAssociate,
      );
    } catch (_) {
      throw new Error(t("user:link-user-to-retreats-error"));
    }
  }

  async updateUserPermissions(
    login: string,
    permissions: Record<UserPermission, boolean>,
  ): Promise<void> {
    try {
      const axiosInstance = createServiceHandler();
      axiosRetry(axiosInstance, { retries: 5, retryDelay: () => 1000 });

      const userPermissions = Object.entries(permissions)
        .filter(
          ([permissionLabel, isEnabled]) =>
            isEnabled && permissionLabel !== UserPermission.CanAccessBenchmark,
        )
        .map(([permissionLabel]) => permissionLabel);

      await axiosInstance.put(`${URL_BASE}/usuarios/${login}/permissoes`, {
        permissoes: userPermissions,
      });
    } catch (_) {
      throw new Error(t("user:assign-user-permissions-error"));
    }
  }

  async activateUser(userId: number, clientId: number | null) {
    const response = await this.put(`${URL_BASE}/usuarios/clientes/ativar`, {
      idUsuario: userId,
      idCliente: clientId,
    });
    return response.data;
  }

  async inactivateUser(userId: number, clientId: number | null) {
    const response = await this.put(`${URL_BASE}/usuarios/clientes/desativar`, {
      idUsuario: userId,
      idCliente: clientId,
    });
    return response.data;
  }

  async changePassword({ email, userId }: { email: string; userId?: number }) {
    const passwordData = { email, idUsuario: userId };
    const response = await this.post(
      `${URL_BASE}/auth/reset-password`,
      passwordData,
    );

    return response.data;
  }
}

const userService = new UserService();
export default userService;
